import { axiosInstance, axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { tokenClient } from '@/services/api/utils/tokenClient';
import * as authStorage from '@/services/api/utils/authStorage';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { loginSchema } from '@/models/loginSchema';
import { AuthToken, Login, User, UserPrivilege } from '@/types/models';
import { USER_ROLE } from '@/constants/user';
import { getError } from '@/utils/error-report/getError';
import axios, { HttpStatusCode } from 'axios';
import { showToast } from '@/utils/showToast';
import { ThunkConfig } from '@/types/store';
import { userSchema } from '@/models/userSchema';
import { ValidationError } from '@/utils/error-report/errorReporting';
import { authTokenSchema } from '@/models/authTokenSchema';
import { z } from 'zod';
import { serializeDate } from '@/utils/time/serializeDate';
import { hasPrivilege } from './user.utils';
import { closeModal, openLoggedInMenuModal } from '../modal/modal.slice';
import { apiPath } from '../apiPath';

const getUserRole = (privileges: UserPrivilege[]): string => {
  if (hasPrivilege(privileges, 'IS_ADMIN')) {
    return USER_ROLE.ADMIN;
  }
  if (hasPrivilege(privileges, 'IS_CURATOR')) {
    return USER_ROLE.CURATOR;
  }

  return USER_ROLE.USER;
};

const getUserData = async (userId: number): Promise<User> => {
  const response = await axiosInstanceNewAPI.get<User>(apiPath.user(userId));

  return response.data;
};

const getUserTokens = async (userId: number): Promise<AuthToken[]> => {
  const response = await axiosInstanceNewAPI.get<AuthToken[]>(apiPath.authTokens(userId));
  const isDataValid = validateDataUsingZodSchema(response.data, z.array(authTokenSchema));

  if (isDataValid) {
    return response.data;
  }
  return [];
};

const MISSING_TOKENS_MESSAGE =
  'Login response is missing authentication tokens. Check the server api.secretKey configuration.';

/** Stores the session from a login response; returns false (storing nothing) when the response is invalid. */
const storeLoginResponse = (data: Login): boolean => {
  if (!validateDataUsingZodSchema(data, loginSchema)) {
    return false;
  }

  authStorage.setSession({
    login: data.login,
    token: data.token,
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    accessTokenExpiresIn: data.accessTokenExpiresIn,
    userId: data.userId,
  });
  return true;
};

export const login = createAsyncThunk(
  'user/login',
  async (credentials: { login: string; password: string }, { dispatch }) => {
    try {
      const searchParams = new URLSearchParams(credentials);
      const { data } = await axiosInstance.post<Login>(apiPath.postLogin(), searchParams);

      const loginName = data.login;

      if (!storeLoginResponse(data)) {
        return Promise.reject(
          getError({
            error: new Error(MISSING_TOKENS_MESSAGE),
            prefix: 'Login',
          }),
        );
      }

      const userData = await getUserData(data.userId);
      const role = getUserRole(userData.privileges);

      const authTokens = await getUserTokens(userData.id);

      if (role !== USER_ROLE.ADMIN && role !== USER_ROLE.CURATOR) {
        dispatch(closeModal());
      } else {
        dispatch(openLoggedInMenuModal());
      }

      return {
        login: loginName,
        role,
        userData,
        token: data.token,
        authTokens,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === HttpStatusCode.Unauthorized) {
          showToast({
            type: 'error',
            message: 'Invalid credentials.',
          });
          return undefined;
        }
        if (error?.response?.status === HttpStatusCode.Forbidden) {
          showToast({
            type: 'error',
            message:
              error.response.data?.reason ??
              'This user account is inactive and needs to be activated by platform administrators.',
          });
          return undefined;
        }
      }
      return Promise.reject(getError({ error, prefix: 'Login' }));
    }
  },
);

/**
 * After a successful OAuth (ORCID) login the server redirects back with a one-time code, which is
 * exchanged here for the same session data a regular login returns. Resolves to true when the
 * session was stored.
 */
export const exchangeOAuthCode = createAsyncThunk(
  'user/exchangeOAuthCode',
  async (code: string): Promise<boolean> => {
    let data: Login;
    try {
      ({ data } = await tokenClient.post<Login>(apiPath.tokenOAuth(), { code }));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === HttpStatusCode.Unauthorized) {
          showToast({
            type: 'error',
            message:
              'Login via ORCID failed: the login link is invalid or has expired. Please try again.',
          });
          return false;
        }
        if (error?.response?.status === HttpStatusCode.Conflict) {
          // handled here, otherwise the error middleware reports it as a concurrent modification
          showToast({
            type: 'error',
            message:
              error.response.data?.reason ??
              'Login via ORCID failed: the server cannot issue authentication tokens.',
          });
          return false;
        }
      }
      return Promise.reject(getError({ error, prefix: 'Login via ORCID' }));
    }

    if (!storeLoginResponse(data)) {
      return Promise.reject(
        getError({
          error: new Error(MISSING_TOKENS_MESSAGE),
          prefix: 'Login via ORCID',
        }),
      );
    }
    return true;
  },
);

export const requestResetPassword = createAsyncThunk(
  'user/login',
  async ({ userLogin, adminEmail }: { userLogin: string; adminEmail: string | undefined }) => {
    try {
      await axiosInstanceNewAPI.post<string>(apiPath.postRequestResetPassword(userLogin));
      showToast({
        type: 'info',
        message: `Request sent successfully. Please check your email for further instructions.`,
        duration: 30000,
      });
      return null;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        let message = error?.response?.data?.reason;
        if (adminEmail) {
          message += `. Please contact <a href="mailto:${adminEmail}" style="text-decoration: underline; color: #0055bb" target="_blank">system administrator</a> for further assistance.`;
        }
        showToast({
          type: 'error',
          message: `Cannot reset password. ${message}`,
          duration: 30000,
        });
        return null;
      }
      return Promise.reject(getError({ error, prefix: 'Password reset' }));
    }
  },
);

export const registerUser = createAsyncThunk(
  'user/registerUser',
  async ({
    login: userLogin,
    name,
    surname,
    email,
    password,
    adminEmail,
  }: {
    login: string;
    name: string;
    surname: string;
    email: string;
    password: string;
    adminEmail: string | undefined;
  }) => {
    try {
      await axiosInstanceNewAPI.post(apiPath.registerUser(), {
        login: userLogin,
        name,
        surname,
        email,
        password,
        termsOfUseConsent: false,
        active: false,
        connectedToLdap: false,
      });
      showToast({
        type: 'success',
        message:
          'Registration request sent successfully. Please check your email for further instructions.',
        duration: 30000,
      });
      return null;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        let message = error?.response?.data?.reason ?? 'Unable to register account.';
        if (adminEmail) {
          message += `. Please contact <a href="mailto:${adminEmail}" style="text-decoration: underline; color: #0055bb" target="_blank">system administrator</a> for further assistance.`;
        }
        showToast({
          type: 'error',
          message: `Cannot register account. ${message}`,
          duration: 30000,
        });
        return null;
      }
      return Promise.reject(getError({ error, prefix: 'Registration' }));
    }
  },
);

export const confirmResetPassword = createAsyncThunk(
  'user/confirmResetPassword',
  async ({ token, password }: { token: string; password: string }) => {
    try {
      await axiosInstanceNewAPI.post(apiPath.postResetPassword(), { token, password });
      showToast({
        type: 'success',
        message: 'Password has been reset successfully. You can now log in.',
        duration: 10000,
      });
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error?.response?.data?.reason ?? 'Invalid or expired token.';
        showToast({
          type: 'error',
          message: `Cannot reset password. ${message}`,
          duration: 10000,
        });
        return false;
      }
      return Promise.reject(getError({ error, prefix: 'Password reset' }));
    }
  },
);

export const confirmEmail = createAsyncThunk(
  'user/confirmEmail',
  async ({ login: userLogin, token }: { login: string; token: string }) => {
    try {
      const { data } = await axiosInstanceNewAPI.post<{ message: string; status: string }>(
        apiPath.postConfirmEmail(userLogin, token),
      );
      return data?.message ?? null;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error?.response?.data?.reason ?? 'Invalid or expired token.';
        return Promise.reject(new Error(message));
      }
      return Promise.reject(getError({ error, prefix: 'Email confirmation' }));
    }
  },
);

export const restoreSession = createAsyncThunk('user/restoreSession', async () => {
  const userId = authStorage.getUserId();
  if (!userId || !authStorage.getRefreshToken()) {
    return undefined;
  }

  const userData = await getUserData(userId);
  const role = getUserRole(userData.privileges);
  const authTokens = await getUserTokens(userData.id);

  return {
    login: userData.login,
    userData,
    role,
    token: authStorage.getToken(),
    authTokens,
  };
});

export const logout = createAsyncThunk('user/logout', async () => {
  try {
    const refreshToken = authStorage.getRefreshToken();
    if (refreshToken) {
      try {
        await tokenClient.post(apiPath.tokenRevoke(), { refreshToken });
      } catch {
        // revoke failing shouldn't block local logout
      }
    }

    authStorage.clearSession();
    return undefined;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: 'Log out' }));
  }
});

export const updateUser = createAsyncThunk<undefined, User, ThunkConfig>(
  'users/updateUser',
  // eslint-disable-next-line consistent-return
  async user => {
    try {
      const newUser = await axiosInstanceNewAPI.put<User>(apiPath.updateUser(user.id), {
        login: user.login,
        name: user.name,
        surname: user.surname,
        email: user.email ?? '',
        orcidId: user.orcidId,
        connectedToLdap: user.connectedToLdap,
        active: user.active,
        termsOfUseConsent: user.termsOfUseConsent,
        ...(user.password ? { password: user.password } : {}),
      });

      validateDataUsingZodSchema(newUser.data, userSchema);
      if (user.password !== undefined) {
        showToast({ type: 'success', message: 'Profile updated successfully' });
      } else {
        showToast({ type: 'success', message: 'ToS agreement registered' });
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_BAD_REQUEST') {
        const data = error.response?.data;
        return Promise.reject(new ValidationError(data.reason));
      }
      return Promise.reject(getError({ error }));
    }
  },
);

export const updateUserEmail = createAsyncThunk<undefined, User, ThunkConfig>(
  'users/updateUser',
  // eslint-disable-next-line consistent-return
  async user => {
    try {
      const newUser = await axiosInstanceNewAPI.put<User>(apiPath.updateUser(user.id), {
        login: user.login,
        active: user.active,
        connectedToLdap: user.connectedToLdap,
        email: user.email ?? '',
        name: user.name,
        orcidId: user.orcidId,
        surname: user.surname,
        termsOfUseConsent: user.termsOfUseConsent,
      });
      validateDataUsingZodSchema(newUser.data, userSchema);
    } catch (error) {
      return Promise.reject(getError({ error, prefix: 'Update email' }));
    }
  },
);

export const getUserAuthTokens = createAsyncThunk('user/authTokens', async (userId: number) => {
  try {
    return getUserTokens(userId);
  } catch (error) {
    return Promise.reject(getError({ error, prefix: 'Update email' }));
  }
});

export const disableAuthToken = createAsyncThunk(
  'user/authTokens/delete',
  async (authToken: AuthToken, { dispatch }) => {
    try {
      await axiosInstanceNewAPI.delete(apiPath.deleteToken(authToken.userId, authToken.id));
      dispatch(getUserAuthTokens(authToken.userId));
      showToast({
        type: 'info',
        message: authToken.active ? `API token has been disabled` : `API token has been removed`,
      });
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(getError({ error, prefix: 'Update email' }));
    }
  },
);

type AuthTokenProps = {
  userId: number;
  name: string;
  expirationDate: Date;
};
export const addAuthToken = createAsyncThunk(
  'user/authTokens/add',
  async (authToken: AuthTokenProps) => {
    try {
      const response = await axiosInstanceNewAPI.post(apiPath.addToken(authToken.userId), {
        name: authToken.name,
        expirationDate: serializeDate(authToken.expirationDate),
      });

      const isDataValid = validateDataUsingZodSchema(response.data, authTokenSchema);

      if (isDataValid) {
        showToast({
          type: 'info',
          message: `API token has been added`,
        });
        return response.data;
      }
      return Promise.reject(new Error('Invalid response from server.'));
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Conflict) {
        const data = error.response?.data;
        showToast({ type: 'error', message: data.message, duration: 120000 });
        return Promise.resolve();
      }
      return Promise.reject(getError({ error, prefix: 'Failed to add token' }));
    }
  },
);
